import { useRouteError, isRouteErrorResponse, useNavigate } from 'react-router-dom';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from '../ui/Button';
import './ErrorBoundary.css';

/**
 * Route-level Error Boundary for React Router Data Router.
 * Catches route render errors and provides recovery actions (Reload / Dashboard)
 * with a branded, courteous interface.
 */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();

  let errorMessage = 'An unexpected error occurred.';
  let errorStatus = '';

  if (isRouteErrorResponse(error)) {
    errorStatus = `${error.status} ${error.statusText}`;
    errorMessage = error.data?.message || error.statusText || errorMessage;
  } else if (error instanceof Error) {
    errorMessage = error.message;
  } else if (typeof error === 'string') {
    errorMessage = error;
  }

  return (
    <div className="crash-screen" role="alert" data-testid="route-error-boundary">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <AlertTriangle size={28} color="var(--color-danger)" />
        <h1 style={{ margin: 0 }}>Application Error</h1>
      </div>
      <p style={{ marginTop: 8 }}>
        The requested screen encountered a problem. You can refresh the page or return to the dashboard.
      </p>
      {errorStatus && <p style={{ fontWeight: 600 }}>Status: {errorStatus}</p>}
      <pre className="crash-detail">{errorMessage}</pre>
      <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
        <Button variant="primary" size="sm" onClick={() => window.location.reload()}>
          <RefreshCw size={14} style={{ marginRight: 6, display: 'inline' }} />
          Reload
        </Button>
        <Button variant="secondary" size="sm" onClick={() => navigate('/')}>
          <Home size={14} style={{ marginRight: 6, display: 'inline' }} />
          Dashboard
        </Button>
      </div>
    </div>
  );
}
